/**
 * The Help & feedback conversation as a pure reducer (SA-1310 spec §4 to §9): the turns shown, what
 * the panel waits for, the live summary card, and the bodies the wire sends. No React, no network.
 */
import { withoutChips, type ChipId } from '@/services/feedbackContext/chips'
import type { FeedbackSnapshot } from '@/services/feedbackContext/environment'
import type {
  HelpSource, OneStepSendBody, PageInfo, ReportRead, SendBody, TurnAssessment, TurnBody,
} from '@/services/helpFeedback/appFeedbackApi'
import {
  ASK_ANOTHER_WAY_LINE, CARD_LEAD, CARD_UPDATED_LEAD, CHIP_REPLIES, CHIP_WORDS, GUIDANCE_THANKS, NOT_FOUND_LEAD, NOT_FOUND_OFFER,
  NOT_QUITE_LEAD, TURN_FAILED,
  pageWords, type CardKind, type ChipKey,
} from './helpFeedbackText'

export interface Opener {
  failure?: { message: string }
}

export interface Card {
  kind: CardKind
  summary: string
  attempting: string
}

export type Turn =
  | { id: number; type: 'person'; text: string; spoken: boolean; chip: boolean }
  | { id: number; type: 'assistant'; text: string; question?: string | null }
  | {
      id: number; type: 'answer'; markdown: string; streaming: boolean; resolved?: 'yes' | 'no'
      /** The help it read (SA-1314), shown under it unless the question was spoken. */
      sources?: HelpSource[]; spoken?: boolean
      /** The task tour "Show me" plays (SA-1316). */
      tour?: string
    }
  /** The help had no answer: one tap sends the question, or the person asks another way (SA-1314). */
  | { id: number; type: 'notFound'; text: string; card: Card; settled: boolean }
  | { id: number; type: 'card'; lead: string; card: Card; dimmed: boolean }

export interface Conversation {
  opener: Opener
  /** The page's title without the app name, '' when unknown. */
  page: string
  snapshot: FeedbackSnapshot
  removed: ChipId[]
  feedbackId: string | null
  turns: Turn[]
  waiting: 'none' | 'turn' | 'send'
  assessment: TurnAssessment | null
  addingMore: boolean
  sent: ReportRead | null
  guidanceSent: boolean
  sendError: string | null
  startedAt: number
  nextId: number
}

export type Action =
  | { type: 'chip'; chip: ChipKey }
  | { type: 'personSaid'; text: string; spoken?: boolean }
  | { type: 'draft'; feedbackId: string }
  | { type: 'token'; token: string }
  /** `oneTap` false (no send possible) keeps SA-1313's editable card on a "no". */
  | { type: 'turn'; turn: TurnAssessment; oneTap?: boolean }
  | { type: 'turnFailed' }
  | { type: 'say'; text: string }
  | { type: 'cardEdit'; patch: Partial<Card> }
  | { type: 'addMore' }
  | { type: 'removeChip'; chip: ChipId }
  | { type: 'restoreChip'; chip: ChipId }
  | { type: 'sending' }
  | { type: 'sent'; report: ReportRead }
  | { type: 'sendFailed'; reason: string }
  | { type: 'guidanceResolved' }
  | { type: 'notQuite' }
  | { type: 'askAnotherWay' }

function latestNav(snapshot: FeedbackSnapshot) {
  for (let i = snapshot.events.length - 1; i >= 0; i--) if (snapshot.events[i].kind === 'nav') return snapshot.events[i]
  return undefined
}

export function startConversation(opener: Opener, snapshot: FeedbackSnapshot, now: number): Conversation {
  const nav = latestNav(snapshot)
  return {
    opener,
    page: pageWords(typeof nav?.title === 'string' ? nav.title : ''),
    snapshot,
    removed: [],
    feedbackId: null,
    turns: [],
    waiting: 'none',
    assessment: null,
    addingMore: false,
    sent: null,
    guidanceSent: false,
    sendError: null,
    startedAt: now,
    nextId: 1,
  }
}

/** The person's own words (never a chip's), in order. */
function personWords(c: Conversation): Array<{ text: string; spoken: boolean }> {
  return c.turns.flatMap((t) => (t.type === 'person' && !t.chip ? [{ text: t.text, spoken: t.spoken }] : []))
}

export function hasPersonWords(c: Conversation): boolean {
  return personWords(c).length > 0
}

/** Anything the person said, a chip included: the opening's chips go once there is. */
export function hasSpoken(c: Conversation): boolean {
  return c.turns.some((t) => t.type === 'person')
}

export function firstWords(c: Conversation): string {
  return personWords(c)[0]?.text ?? ''
}

/** Unsent, with the person's own words: what the resume offer is for. */
export function isResumable(c: Conversation | null): c is Conversation {
  return !!c && !c.sent && !c.guidanceSent && hasPersonWords(c)
}

/** The live (newest, not dimmed) card, if any. */
export function liveCard(c: Conversation): Card | null {
  for (let i = c.turns.length - 1; i >= 0; i--) {
    const t = c.turns[i]
    if (t.type === 'card') return t.dimmed ? null : t.card
  }
  return null
}

/** The newest card shown, dimmed or not. */
export function lastCard(c: Conversation): Card | null {
  for (let i = c.turns.length - 1; i >= 0; i--) {
    const t = c.turns[i]
    if (t.type === 'card') return t.card
  }
  return null
}

/** A card from the person's own words: a Problem, editable. */
export function fallbackCard(c: Conversation): Card {
  return { kind: 'problem', summary: personWords(c).map((w) => w.text).join('\n\n').slice(0, 2000), attempting: '' }
}

function cardFrom(a: TurnAssessment, kind?: CardKind): Card {
  const k: CardKind = kind ?? (a.kind === 'guidance' ? 'question' : (a.kind as CardKind)) ?? 'question'
  const known: CardKind[] = ['problem', 'idea', 'question', 'praise']
  return { kind: known.includes(k) ? k : 'question', summary: a.summary ?? '', attempting: a.attempting ?? '' }
}

function pageInfo(c: Conversation): PageInfo {
  if (c.removed.includes('page')) return null
  const nav = latestNav(c.snapshot)
  if (!nav) return null
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { kind, at, ...rest } = nav
  return rest
}

function contextOf(c: Conversation) {
  return withoutChips(c.snapshot, c.removed)
}

export function turnBody(c: Conversation, message: string, spoken: boolean): TurnBody {
  return {
    ...(c.feedbackId ? { feedback_id: c.feedbackId } : {}),
    message,
    spoken,
    page: pageInfo(c),
    context: contextOf(c),
  }
}

/** What Send sends for a card: the draft's send, or the one step when no draft exists yet. */
export function sendBody(c: Conversation, card: Card = liveCard(c) ?? lastCard(c) ?? fallbackCard(c)):
  | { draft: string; body: SendBody }
  | { draft: null; body: OneStepSendBody } {
  const body: SendBody = {
    kind: card.kind,
    summary: card.summary.trim() || null,
    attempting: card.attempting.trim() || null,
    page: pageInfo(c),
    context: contextOf(c),
    removed: [...c.removed],
    resolved_by_guidance: false,
  }
  if (c.feedbackId) return { draft: c.feedbackId, body }
  const first = personWords(c)[0]
  return { draft: null, body: { ...body, message: first?.text || card.summary || '…', spoken: first?.spoken ?? false } }
}

/** "Yes, that sorted it": the question noted as guidance, resolved. */
export function guidanceBody(c: Conversation): SendBody {
  return {
    kind: 'guidance',
    summary: c.assessment?.summary ?? null,
    attempting: c.assessment?.attempting ?? null,
    page: pageInfo(c),
    context: contextOf(c),
    removed: [...c.removed],
    resolved_by_guidance: true,
  }
}

type NewTurn = Turn extends infer T ? (T extends Turn ? Omit<T, 'id'> : never) : never

function push(c: Conversation, ...turns: NewTurn[]): Conversation {
  let id = c.nextId
  const added = turns.map((t) => ({ ...t, id: id++ }) as Turn)
  return { ...c, turns: [...c.turns, ...added], nextId: id }
}

function dimCards(turns: Turn[]): Turn[] {
  return turns.map((t) => (t.type === 'card' && !t.dimmed ? { ...t, dimmed: true } : t))
}

function withCard(c: Conversation, card: Card, lead?: string): Conversation {
  const hadCard = c.turns.some((t) => t.type === 'card')
  const next = { ...c, turns: dimCards(c.turns), addingMore: false, sendError: null }
  return push(next, { type: 'card', lead: lead ?? (hadCard ? CARD_UPDATED_LEAD : CARD_LEAD), card, dimmed: false })
}

/** Whether the newest thing the person said was spoken. */
function lastSpoken(turns: Turn[]): boolean {
  for (let i = turns.length - 1; i >= 0; i--) {
    const t = turns[i]
    if (t.type === 'person') return t.spoken
  }
  return false
}

/** The open not-found offer's card, if the newest turn is one. */
export function openOffer(c: Conversation): Card | null {
  const last = c.turns[c.turns.length - 1]
  return last?.type === 'notFound' && !last.settled ? last.card : null
}

function settleOffers(turns: Turn[]): Turn[] {
  return turns.map((t) => (t.type === 'notFound' && !t.settled ? { ...t, settled: true } : t))
}

function lastAnswerIndex(turns: Turn[]): number {
  for (let i = turns.length - 1; i >= 0; i--) if (turns[i].type === 'answer') return i
  return -1
}

export function reduce(c: Conversation, action: Action): Conversation {
  switch (action.type) {
    case 'chip': {
      const said = push(c, { type: 'person', text: CHIP_WORDS[action.chip], spoken: false, chip: true })
      if (action.chip === 'explain') return { ...said, waiting: 'turn' }
      return push(said, { type: 'assistant', text: CHIP_REPLIES[action.chip] })
    }
    case 'personSaid':
      return { ...push({ ...c, turns: settleOffers(c.turns) }, { type: 'person', text: action.text, spoken: !!action.spoken, chip: false }), waiting: 'turn' }
    case 'draft':
      return { ...c, feedbackId: action.feedbackId }
    case 'token': {
      const last = c.turns[c.turns.length - 1]
      if (last?.type === 'answer' && last.streaming) {
        return { ...c, turns: [...c.turns.slice(0, -1), { ...last, markdown: last.markdown + action.token }] }
      }
      return push(c, { type: 'answer', markdown: action.token, streaming: true })
    }
    case 'turn': {
      const a = action.turn
      const base: Conversation = { ...c, waiting: 'none', assessment: a }
      if (a.mode === 'answer') {
        // The reply is never shown beside an answer, and an empty answer never reaches the
        // person: with nothing found it becomes an offer to ask the team (SA-1313).
        const last = base.turns[base.turns.length - 1]
        const streamed = last?.type === 'answer' && last.streaming ? last : null
        const markdown = streamed?.markdown.trim() ?? ''
        const turns = streamed ? base.turns.slice(0, -1) : base.turns
        const sources = a.found === 'no' ? [] : (a.sources ?? [])
        const settled: Turn | null = markdown && streamed
          ? {
              ...streamed, markdown, streaming: false,
              ...(sources.length ? { sources, spoken: lastSpoken(turns) } : {}),
              ...(a.tour && a.found !== 'no' ? { tour: a.tour } : {}),
            }
          : null
        const kept: Conversation = { ...base, turns: settled ? [...turns, settled] : turns }
        if (a.found === 'no') {
          if (action.oneTap === false) return withCard(kept, cardFrom(a, 'question'), NOT_FOUND_LEAD)
          return push(kept, { type: 'notFound', text: NOT_FOUND_OFFER, card: cardFrom(a, 'question'), settled: false })
        }
        if (!markdown) return reduce({ ...c, turns }, { type: 'turnFailed' })
        return kept
      }
      if (a.question) return push(base, { type: 'assistant', text: a.reply, question: a.question })
      const replied = a.reply ? push(base, { type: 'assistant', text: a.reply }) : base
      return withCard(replied, cardFrom(a), base.addingMore ? CARD_UPDATED_LEAD : undefined)
    }
    case 'turnFailed': {
      const said = push({ ...c, waiting: 'none' }, { type: 'assistant', text: TURN_FAILED })
      const current = liveCard(c)
      return withCard(said, current ?? fallbackCard(c), CARD_LEAD)
    }
    case 'say':
      return push({ ...c, waiting: c.waiting === 'turn' ? 'none' : c.waiting }, { type: 'assistant', text: action.text })
    case 'askAnotherWay':
      return push({ ...c, turns: settleOffers(c.turns) }, { type: 'assistant', text: ASK_ANOTHER_WAY_LINE })
    case 'cardEdit': {
      for (let i = c.turns.length - 1; i >= 0; i--) {
        const t = c.turns[i]
        if (t.type === 'card') {
          if (t.dimmed) return c
          const turns = [...c.turns]
          turns[i] = { ...t, card: { ...t.card, ...action.patch } }
          return { ...c, turns }
        }
      }
      return c
    }
    case 'addMore':
      return { ...c, addingMore: true }
    case 'removeChip':
      return c.removed.includes(action.chip) ? c : { ...c, removed: [...c.removed, action.chip] }
    case 'restoreChip':
      return { ...c, removed: c.removed.filter((r) => r !== action.chip) }
    case 'sending':
      return { ...c, waiting: 'send', sendError: null }
    case 'sent':
      return { ...c, waiting: 'none', sent: action.report, sendError: null }
    case 'sendFailed':
      return { ...c, waiting: 'none', sendError: action.reason }
    case 'guidanceResolved': {
      const i = lastAnswerIndex(c.turns)
      const turns = [...c.turns]
      if (i >= 0) turns[i] = { ...(turns[i] as Extract<Turn, { type: 'answer' }>), resolved: 'yes' }
      return push({ ...c, turns, waiting: 'none', guidanceSent: true, sendError: null }, { type: 'assistant', text: GUIDANCE_THANKS })
    }
    case 'notQuite': {
      const i = lastAnswerIndex(c.turns)
      const turns = [...c.turns]
      if (i >= 0) turns[i] = { ...(turns[i] as Extract<Turn, { type: 'answer' }>), resolved: 'no' }
      const card = c.assessment ? cardFrom(c.assessment, 'question') : { ...fallbackCard(c), kind: 'question' as const }
      return withCard({ ...c, turns }, card, NOT_QUITE_LEAD)
    }
  }
}
